import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### FHA Transaction Set 264 (TS264) — Mortgage Delinquency Status Reporting
 *
 * The FHA TS264 report communicates the delinquency/default status of FHA-insured loans.
 * Servicers must report one record per delinquent loan each month, including the mortgage
 * status (DDS code), default reason, occupancy info, and key dates.
 *
 * The FHATS264LoanRecord type represents a single loan's data for the report, stored per-row
 * on the InvestorReporting table. The final output format will be X12 EDI (to be added later);
 * for now, we output raw JSON.
 *
 * DDS codes reference: HUD Handbook 4000.1, FHA Single Family Default Monitoring System (SFDMS)
 *
 * v2 — August 2025 revision of TS 264 X12 004040 Implementation Guide:
 * - Removed: adpCode (REF*60), firstPaymentDueDate (DTP*564), borrowers (Loop 0211),
 *   property address fields (N3/N4 Loop 0212) — all now "Do Not Send"
 * - Added: lastEscrowAnalysisDate (DTP*868), monetary amount fields (AMT PI/TI/EB/AT/HI/FI/FP/FH)
 * - Added: new occupancy codes '09', '13', '16'
 * - Added: new DDS/DDR codes per August 2025 spec
 */

/**
 * ##### Mortgage Status Codes (DDS)
 *
 * These codes describe the current delinquency/default status of an FHA-insured loan.
 * They are reported each cycle as the loan's primary status indicator.
 */
export const FHATS264MortgageStatusCode = z.enum([
  // Delinquency statuses
  '42', // Delinquent — No Action
  '06', // Forbearance (Formal Forbearance Plan)
  '07', // Forbearance (General) — NEW Aug 2025
  '12', // Repayment Plan
  '05', // Repayment Plan (new code) — NEW Aug 2025
  '08', // Trial Modification
  '09', // Pre-Foreclosure Sale Approved (Short Sale)
  '28', // Modification
  '32', // Military Indulgence
  '31', // Probate

  // Delinquency Workouts (AL) — NEW Aug 2025
  '23', // Disaster Forbearance
  '40', // Modification Started 40 Year
  '60', // Outside of the Waterfall Loan Modification (OWL)
  '80', // Borrower Declined Loss Mitigation Consultation
  '81', // Borrower Participated in Loss Mitigation Consultation
  '93', // Combination Loan Modification and Partial Claim/40 Year Modification Started
  '3C', // Trial Payment Plan Standalone Partial Claim
  '3D', // Trial Payment Plan Standalone Loan Modification
  '3E', // Trial Payment Plan Combination Loan Modification/Partial Claim
  '3F', // Trial Payment Plan Payment Supplement
  '3G', // Trial Payment Plan – Exempted Transfer
  '3H', // Trial Payment Plan Outside of the Waterfall Loan Modification (OWL)
  'BB', // Complete Loss Mitigation Request Received
  'BD', // Loss Mitigation Request Denied

  // Foreclosure statuses
  '68', // First Legal Action / Foreclosure Initiated
  '33', // Contested / Litigated Foreclosure

  // Account on Foreclosure Hold — NEW category Aug 2025
  '95', // State Mandated Delay
  '96', // Federal Law Mandated Delay

  // Account in Foreclosure (AF)
  '2Z', // Foreclosure Mediation — NEW Aug 2025

  // Bankruptcy statuses
  '65', // Chapter 7 Bankruptcy Petition Filed
  '67', // Chapter 13 Bankruptcy Petition Filed
  '66', // Chapter 11 Bankruptcy Petition Filed
  '59', // Chapter 12 Bankruptcy Petition Filed
  '69', // Bankruptcy Plan Confirmed
  '75', // Bankruptcy Discharged No Reaffirmation — NEW Aug 2025
  '79', // Bankruptcy Discharged Reaffirmation — NEW Aug 2025

  // Loss mitigation workout codes
  '10', // Partial Claim (Assignment)
  '36', // Special Forbearance — Employment Related
  '37', // Special Forbearance — Non-Employment Related
  '39', // Loan Modification — Formal
  '41', // FHA-HAMP Modification
  '44', // Pre-Foreclosure Sale Completed
  '51', // Deed-in-Lieu of Foreclosure
  '3A', // Partial Claim Filed

  // Post-foreclosure / pre-termination codes
  '2U', // Foreclosure Sale Held — Awaiting Eviction
  '2N', // Foreclosure Sale Held — Property Occupied
  '2G', // Foreclosure Sale Held — Redemption Period
  '2I', // Property Acquired — Third-Party Sale at Foreclosure
  '2R', // Property Conveyed — Awaiting Conveyance
  '2S', // Property Conveyed — Claim Filed

  // Concluding / reinstating statuses
  '20', // Account Reinstated — Current
  '21', // Account Reinstated — Re-performing
  '98', // Account Reinstated — Other
  '89', // Reinstated – Exempted Transfer — NEW Aug 2025
  '99', // Reinstated – Exempted Transfer after Loss Mitigation Intervention — NEW Aug 2025

  // Non-claim terminations
  '13', // Paid in Full
  '29', // Assumption Approved
  '30', // Sold — Third-Party Sale
  '73', // Refinance

  // Claim terminations
  '17', // Claim Paid — Foreclosure
  '46', // Claim Paid — Pre-Foreclosure Sale
  '47', // Claim Paid — Deed-in-Lieu
  '48', // Claim Paid — Partial Claim
  '49', // Claim Paid — Assignment

  // Cancel
  '25', // Cancel Previous Default Report
]);
export type FHATS264MortgageStatusCode = z.infer<typeof FHATS264MortgageStatusCode>;

/**
 * ##### Default Reason Codes (DDR)
 *
 * Codes describing the primary reason for the loan's default. These mirror the QRPC
 * default reason codes used in the Freddie Mac EDR mapping with a few FHA-specific additions.
 */
export const FHATS264DefaultReasonCode = z.enum([
  '001', // Death of Principal Mortgagor
  '002', // Illness of Principal Mortgagor
  '003', // Illness of Mortgagor's Family Member
  '004', // Death of Mortgagor's Family Member
  '005', // Marital Difficulties
  '006', // Curtailment of Income
  '007', // Excessive Obligations
  '008', // Abandonment of Property
  '009', // Distant Employment Transfer
  '011', // Property Problem
  '012', // Inability to Sell Property
  '013', // Inability to Rent Property
  '014', // Military Service
  '015', // Other
  '016', // Unemployment
  '017', // Business Failure
  '019', // Casualty Loss
  '022', // Energy-Environment Cost
  '023', // Servicing Problems
  '026', // Payment Adjustment
  '027', // Payment Dispute
  '029', // Transfer of Ownership Pending
  '030', // Fraud
  '031', // Unable to Contact Borrower
  '055', // National Emergency Declaration
  'INC', // Incarceration

  // NEW Aug 2025 — Disaster-related DDR codes
  'A43', // Disaster Damaged Property
  'A45', // Income Loss Due to Disaster
  'A46', // Unable to Contact Borrower Disaster
]);
export type FHATS264DefaultReasonCode = z.infer<typeof FHATS264DefaultReasonCode>;

/**
 * ##### Occupancy Status Codes
 *
 * Describes the current occupancy state of the property securing the FHA-insured loan.
 * When the property is vacant (code '01'), an occupancy status date is required.
 */
export const FHATS264OccupancyCode = z.enum([
  '01', // Vacant
  '03', // Owner Occupied
  '04', // Tenant Occupied
  '05', // Unknown
  '06', // Abandoned
  '07', // Under Repair / Rehabilitation
  '09', // Vacant Lot — NEW Oct 2025
  '13', // Unable to Determine Occupancy Due to Disaster — NEW Oct 2025
  '16', // Occupied by Borrower Secondary Residence — NEW Oct 2025
]);
export type FHATS264OccupancyCode = z.infer<typeof FHATS264OccupancyCode>;

/**
 * ##### FHA TS264 Loan Record (v2)
 *
 * Represents a single delinquent loan's data for the FHA TS264 report. This is stored
 * per-row on the InvestorReporting table. Each field corresponds to a segment/element
 * in the eventual X12 EDI 264 transaction set.
 *
 * v2 breaking changes (August 2025 spec revision):
 * - Removed: adpCode, firstPaymentDueDate, borrowers, property address fields
 * - Added: lastEscrowAnalysisDate, contractual/escrow/insurance amount fields
 *
 * Serializer rules for the eventual X12 EDI formatter (not yet implemented):
 * - Do NOT emit: MIS (heading 030), N3/N4 (heading Loop 0100), second N1*JU iteration,
 *   PER (Loop 0200 070), N1 (Loop 0211 120), N3/N4 (Loop 0212 190/200),
 *   DTP*564 (Loop 0212 260), REF*60 (Loop 0210), MRC (Loop 0212 270)
 * - DO emit: DTP*868 when lastEscrowAnalysisDate is present,
 *   AMT segments (PI, TI, EB, AT, HI, FI, FP, FH) when corresponding fields are present.
 *   EB (Escrow Balance) can be negative.
 * - SE01 segment count must be dynamically computed from actually-emitted segments.
 */
export const FHATS264LoanRecord = z.object({
  version: z.literal('2').describe('version'),

  // ============================================================
  // Loan identification
  // ============================================================

  /** The last day of the reporting month (e.g. 2025-09-30 for the September cycle) */
  periodEndingDate: zodDateOrString.describe('date'),

  /** The servicer's internal loan number (LOS ID) */
  mortgageLoanNumber: z.string().describe('string'),

  /** FHA case number from loan.configuration.investor.fhaCaseNumber */
  fhaCaseNumber: z.string().describe('string'),

  // ============================================================
  // Occupancy
  // ============================================================

  occupancyCode: FHATS264OccupancyCode,

  /**
   * Required when occupancyCode is '01' (Vacant). The date the vacancy was confirmed.
   * Omitted when the property is not vacant.
   */
  occupancyStatusDate: zodDateOrString.optional().describe('date'),

  // ============================================================
  // Default information
  // ============================================================

  /** The DDR code indicating the primary reason for default */
  defaultReasonCode: FHATS264DefaultReasonCode.optional(),

  /**
   * Unpaid principal balance encoded as integer cents with no decimal point.
   * E.g. $123,456.13 is stored as 12345613.
   */
  unpaidPrincipalBalance: z.number().describe('currency'),

  // ============================================================
  // Mortgage status
  // ============================================================

  /** The DDS code describing the loan's current delinquency/default status */
  mortgageStatusCode: FHATS264MortgageStatusCode,

  /**
   * The date the loan first entered the current DDS status during this default episode.
   * This is NOT the current reporting period date — it reflects when the status originated.
   */
  mortgageStatusDate: zodDateOrString.describe('date'),

  // ============================================================
  // Key dates
  // ============================================================

  /**
   * The due date of the oldest unpaid installment. Always normalized to the 1st of the month.
   * Derived from LPI date (the next payment date after the last paid installment).
   */
  oldestUnpaidInstallmentDate: zodDateOrString.describe('date'),

  // ============================================================
  // New fields — effective 02/02/2026 (optional until then)
  // ============================================================

  /** Date last escrow analysis was performed. Maps to DTP qualifier 868. */
  lastEscrowAnalysisDate: zodDateOrString.optional().describe('date'),

  /** Contractual monthly principal & interest amount in cents. AMT qualifier 'PI'. */
  contractualMonthlyPI: z.number().optional().describe('currency'),

  /** Contractual monthly taxes & insurance amount in cents. AMT qualifier 'TI'. */
  contractualMonthlyTI: z.number().optional().describe('currency'),

  /** Escrow balance in cents (CAN be negative). AMT qualifier 'EB'. */
  escrowBalance: z.number().optional().describe('currency'),

  /** Annual tax amount in cents. AMT qualifier 'AT'. */
  annualTaxAmount: z.number().optional().describe('currency'),

  /** Annual hazard insurance premium in cents. AMT qualifier 'HI'. */
  annualHazardInsurancePremium: z.number().optional().describe('currency'),

  /** Annual flood insurance premium in cents. AMT qualifier 'FI'. */
  annualFloodInsurancePremium: z.number().optional().describe('currency'),

  /** Force-placed flood insurance premium (annualized) in cents. AMT qualifier 'FP'. */
  forcedPlacedFloodInsurancePremiumAnnualized: z.number().optional().describe('currency'),

  /** Force-placed hazard insurance premium (annualized) in cents. AMT qualifier 'FH'. */
  forcedPlacedHazardInsurancePremiumAnnualized: z.number().optional().describe('currency'),
});
export type FHATS264LoanRecord = z.infer<typeof FHATS264LoanRecord>;

/** Alias for InvestorReporting storage — one FHATS264LoanRecord per InvestorReporting row */
export const FHATS264 = FHATS264LoanRecord;
export type FHATS264 = z.infer<typeof FHATS264>;
